/**
 * Status pill — maps workflow states to the DUX CD pill palette.
 *
 * OK/completed states use aqua (brand primary).
 * Signal states (attention needed) use yellow — same accent the marketing
 * site uses for its primary CTAs, which the CRM repurposes to mean
 * "needs your review".
 * Muted states use neutral grey.
 */
import { clsx } from "clsx";

const VARIANTS: Record<string, string> = {
  // Signals that need attention — yellow
  active:          "pill--ok",
  scheduled:       "pill--ok",
  proposed:        "pill--signal",
  awaiting_review: "pill--signal",
  awaiting_manual: "pill--signal",
  pending:         "pill--signal",

  // Terminal good — aqua
  completed:  "pill--ok",
  approved:   "pill--ok",
  converted:  "pill--ok",

  // Scope-group membership roles — lead highlighted, member muted
  lead:   "pill--ok",
  member: "pill--muted",

  // Terminal muted — grey
  dismissed: "pill--muted",
  skipped:   "pill--muted",
  paused:    "pill--muted",
  archived:  "pill--muted",
  draft:     "pill--muted",
  aborted:   "pill--muted",
  removed:   "pill--muted",
  opted_out: "pill--muted",
};

/**
 * Explicit tone, for vocabularies the shared map above doesn't own — e.g. the
 * AI-team state words, where the same word ("working", "failed") belongs to a
 * domain that decides its own colour (`src/lib/aiTeam.ts`).
 */
export type PillTone = "ok" | "signal" | "alert" | "muted";

// Literal class names on purpose: Tailwind keeps an `@layer components` class
// only if the exact string appears in a source file, so a template like
// `pill--${tone}` silently purged `pill--alert` from the build.
const TONE_CLASS: Record<PillTone, string> = {
  ok: "pill--ok",
  signal: "pill--signal",
  alert: "pill--alert",
  muted: "pill--muted",
};

export function Pill({
  value,
  tone,
}: {
  value?: string | null;
  tone?: PillTone;
}) {
  if (!value) return <span className="pill pill--muted">—</span>;
  const variant = tone ? TONE_CLASS[tone] : VARIANTS[value] || "pill--muted";
  return <span className={clsx("pill", variant)}>{value.replace(/_/g, " ")}</span>;
}
